import React, { useMemo } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { LoadZoneObjectName } from "./LoadZoneActualLoadVisual";
import {
  getLoadZoneMaxItems,
  getLoadZoneSeriesColor,
  matchesSelectedIso,
  normalizeObjectName,
  pickTopLoadZoneObjects,
} from "./loadZoneUtils";
import { MARKET_MONITOR_TOOLTIP_PROPS } from "./tooltipConfig";

interface MarketDataRow {
  ISO?: string;
  OBJECTNAME?: string | null;
  DATATYPEID?: number | string | null;
  TIMESTAMP?: string;
  DATETIME?: string;
  DATA_POINTS?: string;
  VALUE?: string | number | null;
  VOLUME?: string | number | null;
}

interface DatatypeRow {
  DATATYPEID?: number | string | null;
  GENERATIONTYPE?: string | null;
}

interface SpreadPoint {
  index: number;
  timestampIso: string;
  timestampLabel: string;
  dateLabel: string;
  [key: string]: string | number;
}

interface LoadZoneGenerationSpreadVisualProps {
  renewableRealtimeRows: MarketDataRow[];
  renewableForecastRows: MarketDataRow[];
  datatypesRows: DatatypeRow[];
  selectedIso: string;
  selectedObjectName: LoadZoneObjectName | null;
  onSelectObjectName: (name: LoadZoneObjectName) => void;
}

const MAX_POINTS = 14 * 24;
const DIMMED_COLOR = "#cbd5e1";
const PJM_COMBINED_OBJECT_NAME = "RTO COMBINED";
const PJM_COMBINED_LABEL = "PJM - RTO Combined";

const toNumber = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const toDatatypeKey = (value: number | string | null | undefined): string | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  return String(value);
};

const toHourKey = (value: string | undefined): string | null => {
  if (!value) {
    return null;
  }

  const normalized = value.includes("T") ? value : value.replace(" ", "T");
  const parsed = new Date(normalized);

  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  parsed.setMinutes(0, 0, 0);
  return parsed.toISOString();
};

const toShortDate = (isoDateTime: string): string => {
  const parsed = new Date(isoDateTime);
  if (Number.isNaN(parsed.getTime())) {
    return isoDateTime;
  }

  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
  });
};

const toFullTimeLabel = (isoDateTime: string): string => {
  const parsed = new Date(isoDateTime);
  if (Number.isNaN(parsed.getTime())) {
    return isoDateTime;
  }

  return parsed.toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const roundValue = (value: number): number => Math.round(value * 100) / 100;

const formatYAxis = (value: number): string => `${Math.round(value / 1000)}K`;

const formatTooltip = (value: number): string =>
  `${value.toLocaleString("en-US", { maximumFractionDigits: 2 })} MW`;

const isPjmIso = (selectedIso: string): boolean => {
  const iso = selectedIso.trim().toUpperCase();
  return iso === "PJM" || iso === "PJMISO";
};

const getSeriesLabel = (objectName: string, selectedIso: string): string => {
  if (isPjmIso(selectedIso) && objectName === PJM_COMBINED_OBJECT_NAME) {
    return PJM_COMBINED_LABEL;
  }

  return objectName;
};

const hasAllowedGenerationType = (
  row: MarketDataRow,
  generationTypeByDatatype: Map<string, string>
): boolean => {
  const datatypeKey = toDatatypeKey(row.DATATYPEID);
  if (!datatypeKey) {
    return false;
  }

  const generationType = generationTypeByDatatype.get(datatypeKey)?.trim().toUpperCase() ?? "";
  return generationType.includes("SOLAR") || generationType.includes("WIND");
};

const buildSpreadData = (
  realtimeRows: MarketDataRow[],
  forecastRows: MarketDataRow[],
  generationTypeByDatatype: Map<string, string>,
  selectedIso: string
): { data: SpreadPoint[]; objectNames: string[] } => {
  const pjmCombinedOnly = isPjmIso(selectedIso);
  const realtimeByHourObject = new Map<string, Map<string, number>>();
  const forecastByHourObject = new Map<string, Map<string, number>>();
  const objectTotals = new Map<string, number>();

  const accumulate = (
    targetMap: Map<string, Map<string, number>>,
    row: MarketDataRow,
    hourKey: string,
    objectName: string,
    value: number
  ) => {
    const hourBucket = targetMap.get(hourKey) ?? new Map<string, number>();
    hourBucket.set(objectName, (hourBucket.get(objectName) ?? 0) + value);
    targetMap.set(hourKey, hourBucket);
  };

  realtimeRows.forEach((row) => {
    if (!matchesSelectedIso(row.ISO, selectedIso)) {
      return;
    }

    if (!hasAllowedGenerationType(row, generationTypeByDatatype)) {
      return;
    }

    const objectName = normalizeObjectName(row.OBJECTNAME);
    if (!objectName) {
      return;
    }

    if (pjmCombinedOnly && objectName !== PJM_COMBINED_OBJECT_NAME) {
      return;
    }

    const hourKey = toHourKey(row.TIMESTAMP ?? row.DATETIME);
    const value = toNumber(row.VALUE ?? row.VOLUME);
    if (!hourKey || value === null) {
      return;
    }

    accumulate(realtimeByHourObject, row, hourKey, objectName, value);
    objectTotals.set(objectName, (objectTotals.get(objectName) ?? 0) + Math.abs(value));
  });

  forecastRows.forEach((row) => {
    if (!matchesSelectedIso(row.ISO, selectedIso)) {
      return;
    }

    if (!hasAllowedGenerationType(row, generationTypeByDatatype)) {
      return;
    }

    const objectName = normalizeObjectName(row.OBJECTNAME);
    if (!objectName) {
      return;
    }

    if (pjmCombinedOnly && objectName !== PJM_COMBINED_OBJECT_NAME) {
      return;
    }

    const dataPoint = row.DATA_POINTS?.toLowerCase().trim();
    if (dataPoint && dataPoint !== "forecast_renewable_gen") {
      return;
    }

    const hourKey = toHourKey(row.TIMESTAMP ?? row.DATETIME);
    const value = toNumber(row.VALUE ?? row.VOLUME);
    if (!hourKey || value === null) {
      return;
    }

    accumulate(forecastByHourObject, row, hourKey, objectName, value);
    objectTotals.set(objectName, (objectTotals.get(objectName) ?? 0) + Math.abs(value));
  });

  const objectNames = pickTopLoadZoneObjects(
    objectTotals,
    selectedIso,
    getLoadZoneMaxItems(selectedIso)
  );

  if (!objectNames.length) {
    return { data: [], objectNames: [] };
  }

  const forecastTimeline = Array.from(forecastByHourObject.keys()).sort();
  const combinedTimeline = Array.from(
    new Set([...realtimeByHourObject.keys(), ...forecastByHourObject.keys()])
  ).sort();

  // Keep load-zone date window aligned to forecast timestamps (Power BI parity).
  const timeline = (forecastTimeline.length > 0 ? forecastTimeline : combinedTimeline).slice(
    -MAX_POINTS
  );

  const data = timeline.map((hourKey, index) => {
    const realtimeBucket = realtimeByHourObject.get(hourKey) ?? new Map<string, number>();
    const forecastBucket = forecastByHourObject.get(hourKey) ?? new Map<string, number>();

    const spreadForObject = (objectName: string): number => {
      const actual = realtimeBucket.has(objectName) ? realtimeBucket.get(objectName)! : null;
      const forecast = forecastBucket.get(objectName) ?? 0;

      // Match Power BI logic: IF actual is blank then 0 else actual - forecast.
      if (actual === null) {
        return 0;
      }

      return roundValue(actual - forecast);
    };

    const point: SpreadPoint = {
      index,
      timestampIso: hourKey,
      timestampLabel: toFullTimeLabel(hourKey),
      dateLabel: toShortDate(hourKey),
    };

    objectNames.forEach((name) => {
      point[name] = spreadForObject(name);
    });

    return point;
  });

  return { data, objectNames };
};

const LoadZoneGenerationSpreadVisual: React.FC<LoadZoneGenerationSpreadVisualProps> = ({
  renewableRealtimeRows,
  renewableForecastRows,
  datatypesRows,
  selectedIso,
  selectedObjectName,
  onSelectObjectName,
}) => {
  const generationTypeByDatatype = useMemo(() => {
    const map = new Map<string, string>();
    datatypesRows.forEach((row) => {
      const datatypeKey = toDatatypeKey(row.DATATYPEID);
      const generationType = row.GENERATIONTYPE?.trim();
      if (datatypeKey && generationType) {
        map.set(datatypeKey, generationType);
      }
    });

    return map;
  }, [datatypesRows]);

  const { data, objectNames } = useMemo(
    () => buildSpreadData(renewableRealtimeRows, renewableForecastRows, generationTypeByDatatype, selectedIso),
    [renewableRealtimeRows, renewableForecastRows, generationTypeByDatatype, selectedIso]
  );

  const hasActiveSelection = !!(selectedObjectName && objectNames.includes(selectedObjectName));

  const getSeriesColor = (name: LoadZoneObjectName): string =>
    hasActiveSelection && selectedObjectName !== name
      ? DIMMED_COLOR
      : getLoadZoneSeriesColor(objectNames.indexOf(name));

  const getSeriesOpacity = (name: LoadZoneObjectName): number =>
    hasActiveSelection && selectedObjectName !== name ? 0.45 : 1;

  const handleLegendClick = (entry: unknown) => {
    const legendEntry = entry as { value?: unknown; dataKey?: unknown };
    const selected = String(legendEntry?.dataKey ?? legendEntry?.value ?? "").toUpperCase();
    if (objectNames.includes(selected)) {
      onSelectObjectName(selected);
    }
  };

  const xTicks = useMemo(
    () => data.filter((item) => item.index % 48 === 0 || item.index === data.length - 1).map((item) => item.index),
    [data]
  );

  if (!data.length || !objectNames.length) {
    return (
      <div className="w-full h-[360px] rounded-xl border border-gray-200 bg-white p-4 flex items-center justify-center text-gray-500">
        No spread data available.
      </div>
    );
  }

  return (
    <div className="w-full h-[360px] xl:h-auto xl:flex-[36] rounded-xl border border-gray-200 bg-white p-4">
      <h2 className="text-lg font-semibold text-gray-700 mb-2">Solar &amp; Wind Actual vs Forecast Spread</h2>

      <ResponsiveContainer width="100%" height="84%">
        <LineChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
          <XAxis
            dataKey="index"
            tickFormatter={(value) => data[value]?.dateLabel ?? ""}
            ticks={xTicks}
            tick={{ fill: "#6b7280", fontSize: 12 }}
          />
          <YAxis
            domain={["auto", "auto"]}
            tickFormatter={formatYAxis}
            tick={{ fill: "#6b7280", fontSize: 12 }}
            label={{ value: "MW", angle: -90, position: "insideLeft", offset: 0 }}
          />
          <ReferenceLine y={0} stroke="#9ca3af" />
          <Tooltip
            {...MARKET_MONITOR_TOOLTIP_PROPS}
            formatter={(value: number) => formatTooltip(value)}
            labelFormatter={(value) => data[value]?.timestampLabel ?? ""}
          />
          <Legend
            verticalAlign="top"
            align="left"
            wrapperStyle={{ paddingBottom: 8, cursor: "pointer" }}
            onClick={handleLegendClick}
            formatter={(value: string) => {
              const normalized = value.toUpperCase();
              const isDimmed = !!(
                hasActiveSelection &&
                objectNames.includes(normalized) &&
                selectedObjectName !== normalized
              );
              const legendLabel = getSeriesLabel(normalized, selectedIso);

              return (
                <span style={{ color: isDimmed ? "#94a3b8" : "#374151", opacity: isDimmed ? 0.65 : 1 }}>
                  {legendLabel}
                </span>
              );
            }}
          />
          {objectNames.map((name) => (
            <Line
              key={name}
              type="monotone"
              dataKey={name}
              name={name}
              stroke={getSeriesColor(name)}
              strokeOpacity={getSeriesOpacity(name)}
              strokeWidth={selectedObjectName === name ? 3.2 : 2.6}
              dot={false}
              onClick={() => onSelectObjectName(name)}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default LoadZoneGenerationSpreadVisual;
